<template>
  <div class="delivery">
    <div class="note">
      <feather-icon
        icon="AlertCircleIcon"
        size="14"
      />
      {{ $t('configurator.transport_manual_help') }}
    </div>

    <div class="block-title">
      {{ $t('configurator.delivery_address') }}
    </div>

    <div class="options">
      <div
        class="option"
        :class="{ selected: selected === 'tecnitex' }"
        @click="selected = 'tecnitex'"
      >
        <span class="option-radio" />
        <div>
          <div class="option-title">
            {{ $t('configurator.pickup_tecnitex') }}
          </div>
          <div class="option-sub">
            {{ $t('configurator.pickup_subtitle') }}
          </div>
        </div>
      </div>
      <div
        v-for="address in addresses"
        :key="address.id"
        class="option"
        :class="{ selected: selected === address.id }"
        @click="selected = address.id"
      >
        <span class="option-radio" />
        <div>
          <div class="option-title">
            {{ address.name }}
          </div>
          <div class="option-sub">
            {{ addressText(address) }}
          </div>
        </div>
      </div>
    </div>

    <a
      v-if="!showForm"
      href="#"
      class="add-link"
      @click.prevent="showForm = true"
    >+ {{ $t('configurator.add_address') }}</a>

    <div
      v-else
      class="new-address"
    >
      <div class="block-title">
        {{ $t('configurator.new_address') }}
      </div>
      <div class="form-grid">
        <div class="field full">
          <label>{{ $t('configurator.address_name') }}</label>
          <b-form-input
            v-model="form.name"
            :state="state('name')"
          />
        </div>
        <div class="field full">
          <label>{{ $t('configurator.address_street') }}</label>
          <b-form-input
            v-model="form.address"
            :state="state('address')"
          />
        </div>
        <div class="field">
          <label>{{ $t('configurator.address_country') }}</label>
          <v-select
            :value="form.country"
            :options="countryOptions"
            label="name"
            :clearable="false"
            @input="changeCountry"
          />
          <small
            v-if="state('country') === false"
            class="text-danger"
          >{{ $t('configurator.required') }}</small>
        </div>
        <div class="field">
          <label>{{ $t('configurator.address_province') }}</label>
          <v-select
            v-if="form.country && form.country.code === 'ES'"
            v-model="form.province"
            :options="provinces"
            label="name"
          />
          <b-form-input
            v-else
            v-model="form.province"
            :disabled="!form.country"
            :state="state('province')"
          />
          <small
            v-if="state('province') === false && form.country && form.country.code === 'ES'"
            class="text-danger"
          >{{ $t('configurator.required') }}</small>
        </div>
        <div class="field">
          <label>{{ $t('configurator.address_town') }}</label>
          <b-form-input
            v-model="form.town"
            :state="state('town')"
          />
        </div>
        <div class="field">
          <label>{{ $t('configurator.address_postal_code') }}</label>
          <b-form-input
            v-model="form.postalCode"
            :state="state('postalCode')"
          />
        </div>
        <div class="field">
          <label>{{ $t('configurator.address_phone') }}</label>
          <b-form-input
            v-model="form.phone"
            :state="state('phone')"
          />
        </div>
      </div>
      <div class="d-flex justify-content-end mt-1">
        <b-button
          variant="outline-secondary"
          class="btn-outline mr-50"
          @click="resetForm"
        >
          {{ $t('configurator.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          class="btn-md"
          @click="saveAddress"
        >
          {{ $t('configurator.save_address') }}
        </b-button>
      </div>
    </div>

    <div class="footer">
      <b-button
        variant="outline-secondary"
        class="btn-outline"
        @click="$emit('cancel')"
      >
        {{ $t('configurator.cancel') }}
      </b-button>
      <b-button
        variant="success"
        class="btn-finish"
        :disabled="!selected || showForm"
        @click="$emit('confirm', selected === 'tecnitex' ? '' : selected)"
      >
        <feather-icon
          icon="CheckIcon"
          size="14"
        />
        {{ $t('configurator.finish_offer') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BButton, BFormInput } from 'bootstrap-vue'
import vSelect from 'vue-select'

const REQUIRED = ['name', 'address', 'country', 'province', 'town', 'postalCode', 'phone']

const emptyForm = () => ({
  name: '', address: '', country: null, province: '', town: '', postalCode: '', phone: '',
})

// Misma lógica que steps/FormAddress.vue (alta de dirección de envío del cliente)
export default {
  components: {
    BButton, BFormInput, vSelect,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selected: null,
      showForm: false,
      submitted: false,
      form: emptyForm(),
    }
  },
  computed: {
    ...mapGetters({
      addresses: 'addresses/getItems',
      countries: 'countries/getCountries',
      provinces: 'provinces/getSelectProvinces',
      lang: 'languages/getCurrentLanguage',
    }),
    countryOptions() {
      return this.countries.map(country => ({ ...country, name: country.title[this.lang] || country.title.es }))
    },
  },
  methods: {
    ...mapActions({
      addAddress: 'addresses/create',
      getAddresses: 'addresses/getListAddress',
    }),
    addressText(address) {
      const province = address.province_object ? address.province_object.name : address.province
      const country = address.country ? address.country.title[this.lang] || address.country.title.es : ''
      return [address.address, address.town, [province, address.postalCode].filter(Boolean).join(' '), country].filter(Boolean).join(', ')
    },
    state(field) {
      if (!this.submitted) return null
      return this.form[field] ? null : false
    },
    changeCountry(country) {
      this.form.country = country
      this.form.province = ''
    },
    resetForm() {
      this.form = emptyForm()
      this.submitted = false
      this.showForm = false
    },
    async saveAddress() {
      this.submitted = true
      if (REQUIRED.some(field => !this.form[field])) return
      const data = new FormData()
      data.append('name', this.form.name)
      data.append('address', this.form.address)
      data.append('town', this.form.town)
      data.append('country_id', this.form.country.id)
      data.append('province', this.form.country.code === 'ES' ? this.form.province.id : this.form.province)
      data.append('postalCode', this.form.postalCode)
      data.append('type', 'shipping')
      data.append('client_id', this.order.client.id)
      data.append('phone', this.form.phone)
      let response
      try {
        response = await this.addAddress({ address: data })
      } catch (e) {
        return
      }
      await this.getAddresses({
        page: 1, per_page: 999, search: '', client: this.order.client.id, type: 'shipping',
      })
      if (response && response.data && response.data.data) this.selected = response.data.data.id
      this.resetForm()
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
.note {
  display: flex;
  align-items: flex-start;
  gap: .375rem;
  margin-bottom: 1rem;
  padding: .5rem .625rem;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  background: #f6f6f6;
  font-size: .75rem;
  font-weight: 500;
  color: #6e6b7b;
}
.block-title {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin-bottom: .75rem;
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: #82868b;
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #f0f0f0;
  }
}
.options {
  display: flex;
  flex-direction: column;
  gap: .5rem;
  max-height: 320px;
  overflow-y: auto;
}
.option {
  display: flex;
  align-items: flex-start;
  gap: .75rem;
  padding: .625rem .875rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 7px;
  background: #fff;
  cursor: pointer;
  transition: all .15s;
  &:hover {
    border-color: #ac1a19;
    background: #fffaf5;
  }
  &.selected {
    border-color: #ac1a19;
    background: #fef3e2;
    .option-radio {
      border-color: #ac1a19;
      &::after {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #ac1a19;
      }
    }
    .option-title {
      color: #ac1a19;
    }
  }
}
.option-radio {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border: 1.5px solid #b8c2cc;
  border-radius: 50%;
}
.option-title {
  font-size: .875rem;
  font-weight: 600;
  color: #1e1e1e;
}
.option-sub {
  font-size: .75rem;
  color: #6e6b7b;
}
.add-link {
  display: inline-block;
  margin-top: .75rem;
  font-size: .8rem;
  font-weight: 600;
  color: #ac1a19;
}
.new-address {
  margin-top: 1.25rem;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .75rem;
  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
  .full {
    grid-column: 1 / -1;
  }
}
.field label {
  display: block;
  margin-bottom: .375rem;
  font-size: .78rem;
  font-weight: 600;
  color: #4d4d4d;
}
.footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}
// Tamaños y colores del prototipo: .btn (10px 22px) y .btn-md (6px 14px)
.btn-outline,
.btn-finish,
.btn-md {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .375rem;
  border-radius: 5px !important;
  font-size: .875rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: .4px;
}
.footer .btn-outline,
.btn-finish {
  height: 34px;
  padding: 0 22px !important;
}
.new-address .btn-outline,
.btn-md {
  padding: 6px 14px !important;
}
.btn-outline {
  border-color: #b8c2cc !important;
  background: transparent !important;
  color: #4d4d4d !important;
  &:hover {
    background: #f0f0f0 !important;
  }
}
.btn-finish {
  border-color: #3a7d44 !important;
  background: #3a7d44 !important;
  background-image: none !important;
  &:hover:not(:disabled) {
    border-color: #15803d !important;
    background: #15803d !important;
  }
}
</style>
